import React, { useEffect, useState } from 'react'
import { H2, H3, Separator, Button, Autosuggest, Toggle, Subheading } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { getPersonLabelMetadata, getLabelAssignments, getPersonName } from '../../../services/personService'
import { getSortedArr } from '../../../util/StringUtil'
import { downloadFile } from '../../../util/FileDownloadUtil'
import { DmcText } from '../../common/DmcText'
import useNotifications from '../../../hooks/useNotifications'
import { usePageTitle } from '../../app/PageTitleContext'

export const config = {
  BATCH_SIZE: 10000,
  FILE_LIMIT: 100000,
}

const NAME_DOWNLOAD_WHITELIST = new Set(['INF_MEMORIES_CHECK'])

export default function AdminLabels() {
  usePageView({ page_detail: 'DMConsole:AdminLabels' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tools.adminLabels'))
  const { error } = useNotifications()
  const [labelList, setLabelList] = useState([])
  const [selectedLabel, setSelectedLabel] = useState('')
  const [lastRetrievedId, setLastRetrievedId] = useState(null)
  const [includeName, setIncludeName] = useState(false)

  async function fetchCsv() {
    const pids = []
    let lastToken = lastRetrievedId
    while (pids.length < config.FILE_LIMIT) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const data = await getLabelAssignments(selectedLabel, config.BATCH_SIZE, lastToken)
        pids.push(...data.list.map((assignment) => assignment.personId))
        if (data.list.length < config.BATCH_SIZE) break
        lastToken = data.list[data.list.length - 1].id
      } catch (err) {
        error(t('tools.adminLabels.fetchError', `Error fetching label assignments: ${err?.message || err}`))
        return
      }
    }

    const nameHeader = includeName ? `,${t('name', 'Name')}` : ''
    const header = `${t('personId', 'Person ID')}${nameHeader}`
    const rows = includeName
      ? await Promise.all(
          pids.map(async (pid) => {
            return getPersonName(pid)
              .then((name) => `${pid},${name}`)
              .catch(() => `${pid},${t('name.failure', 'Error: Failed to retrieve name.')}`)
          })
        )
      : pids
    const csvContent = [header, ...rows].join('\n')
    downloadFile(csvContent, `${selectedLabel}-admin-labels.csv`)

    if (pids.length === config.FILE_LIMIT) {
      setLastRetrievedId(lastToken)
    } else {
      setLastRetrievedId(null)
    }
  }

  useEffect(() => {
    getPersonLabelMetadata()
      .then((metadata) => {
        const labels = getSortedArr(metadata, 'humanReadableValueText', 'desc')
        setLabelList(
          labels.map((label, index) => {
            return {
              key: index,
              primaryText: label.humanReadableValueText || label.valueText,
              label,
            }
          })
        )
      })
      .catch((err) => {
        error(t('tools.adminLabels.metadataFetchError', `Error fetching label metadata: ${err?.message || err}`))
      })
  }, [error, t])

  return (
    <>
      <H2 size="H5">{t('tools.adminLabels', 'Admin Labels')}</H2>
      <Separator size="xxs" />
      <H3 supplementary size="H5">
        {t('tools.adminLabels.description', 'Download a CSV of PIDs for a given admin label.')}
        <Subheading>
          {t(
            'tools.adminLabels.description.subheading',
            `Data will be downloaded in batches with a maximum size of ${config.FILE_LIMIT}.`
          )}
        </Subheading>
      </H3>
      <Separator size="sm" />
      {NAME_DOWNLOAD_WHITELIST.has(selectedLabel) && (
        <>
          <Toggle
            label={t('tools.adminLabels.includeName', 'Include Person name in CSV')}
            checked={includeName}
            onChange={() => setIncludeName((prev) => !prev)}
          />
          <DmcText
            text={t('tools.adminLabels.includeName.note', 'Note: This option is only permitted for certain labels.')}
          />
          <Separator size="sm" />
        </>
      )}
      <Autosuggest
        label={t('tools.adminLabels.selectLabel', 'Label')}
        suggestions={labelList}
        clearable
        onSuggestionSelected={(suggestion) => {
          setSelectedLabel(suggestion ? suggestion.label.valueText : '')
          setIncludeName(NAME_DOWNLOAD_WHITELIST.has(suggestion?.label.valueText))
          setLastRetrievedId(null)
        }}
      />
      {lastRetrievedId && (
        <>
          <DmcText
            text={t('tools.adminLabels.batchNotification', `Downloaded batch of ${config.FILE_LIMIT} persons.`)}
          />
          <Separator size="xxs" />
        </>
      )}
      <Button emphasis="high" disabled={!selectedLabel} onClick={fetchCsv}>
        {lastRetrievedId
          ? t('tools.adminLabels.downloadNext', 'Download Next Batch')
          : t('tools.adminLabels.download', 'Download CSV')}
      </Button>
    </>
  )
}
